// Auth client for the WorkOS custom-UI flow. The auth server (Hono) holds the
// WORKOS_API_KEY and talks to WorkOS; the SPA only POSTs credentials and reads
// the session (user + short-lived access token) back.

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface SessionResponse {
  user: SessionUser | null;
  accessToken?: string;
  expiresAt?: number;
}

export type AuthResult =
  | { ok: true; user: SessionUser; accessToken?: string }
  | { ok: false; error: string };

export type OtpChannel = "sms" | "whatsapp";

export type OtpResult =
  | { ok: true; to?: string; phoneVerificationToken?: string }
  | { ok: false; error: string };

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Fetch the current session (user + access token), or a null user. */
export async function fetchSession(): Promise<SessionResponse> {
  try {
    const response = await fetch("/api/auth/get-session", { credentials: "include" });
    if (!response.ok) return { user: null };
    return ((await response.json()) as SessionResponse) || { user: null };
  } catch {
    return { user: null };
  }
}

async function toAuthResult(response: Response): Promise<AuthResult> {
  let data: {
    user?: SessionUser;
    accessToken?: string;
    error?: string;
    requiresEmailVerification?: boolean;
  } = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (response.ok && data.user) {
    return { ok: true, user: data.user, accessToken: data.accessToken };
  }
  // The account was created but sign-in needs email verification first.
  if (data.requiresEmailVerification) {
    return { ok: false, error: "email_verification_required" };
  }
  return { ok: false, error: data.error || "authentication_failed" };
}

export async function loginWithPassword(email: string, password: string): Promise<AuthResult> {
  return toAuthResult(await postJson("/api/auth/login", { email, password }));
}

export async function registerWithPassword(input: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  phoneVerificationToken?: string;
}): Promise<AuthResult> {
  return toAuthResult(await postJson("/api/auth/register", input));
}

export async function requestPhoneOtp(
  phone: string,
  channel: OtpChannel = "sms",
): Promise<OtpResult> {
  try {
    const res = await postJson("/api/auth/otp/send", { phone, channel });
    const data = (await res.json().catch(() => ({}))) as { error?: string; to?: string };
    if (!res.ok) return { ok: false, error: data.error ?? "otp_failed" };
    return { ok: true, to: data.to };
  } catch {
    return { ok: false, error: "otp_failed" };
  }
}

export async function verifyPhoneOtp(phone: string, code: string): Promise<OtpResult> {
  try {
    const res = await postJson("/api/auth/otp/check", { phone, code });
    const data = (await res.json().catch(() => ({}))) as {
      error?: string;
      phoneVerificationToken?: string;
    };
    if (!res.ok || !data.phoneVerificationToken) {
      return { ok: false, error: data.error ?? "invalid_code" };
    }
    return { ok: true, phoneVerificationToken: data.phoneVerificationToken };
  } catch {
    return { ok: false, error: "otp_failed" };
  }
}

export async function requestPasswordReset(email: string): Promise<boolean> {
  try {
    const res = await postJson("/api/auth/forgot-password", { email });
    return res.ok;
  } catch {
    return false;
  }
}

export async function resetPassword(token: string, password: string): Promise<AuthResult | boolean> {
  try {
    const res = await postJson("/api/auth/reset-password", { token, password });
    if (res.ok) return true;
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    return { ok: false, error: data.error || "reset_failed" };
  } catch {
    return { ok: false, error: "reset_failed" };
  }
}

/** Refresh the sealed session server-side. */
export async function refreshToken(): Promise<boolean> {
  try {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Navigate to the custom login page. */
export function login(): void {
  window.location.href = "/login";
}

/** Clear the session and return home. */
export function logout(): void {
  window.location.href = "/api/auth/logout";
}
